import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError, type JobInfo } from "@/lib/api";

/** Give up after this many consecutive failed polls (~20s at 2s intervals). */
const MAX_CONSECUTIVE_FAILURES = 10;

export type JobPollingState = {
  job: JobInfo | null;
  loading: boolean;
  /** Set when polling stopped without reaching a terminal state. */
  error: { code: string; message: string } | null;
};

export function useJobPolling(jobId: string | null): JobPollingState {
  const [job, setJob] = useState<JobInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  const poll = useCallback(async (): Promise<"done" | "pending" | "failed"> => {
    if (!jobId) return "done";
    try {
      const data = await apiFetch<JobInfo>(`/api/jobs/${jobId}`);
      setJob(data);
      if (["completed", "failed", "cancelled"].includes(data.state)) {
        setLoading(false);
        return "done";
      }
      return "pending";
    } catch (cause) {
      // 404/403 are permanent for this job — it was trashed elsewhere, or the
      // caller lost access. Retrying every 2s forever just spins the card.
      if (cause instanceof ApiError && (cause.status === 404 || cause.status === 403)) {
        setLoading(false);
        setError({
          code: cause.status === 404 ? "job_not_found" : "forbidden",
          message:
            cause.status === 404
              ? "This generation is no longer available."
              : "You no longer have access to this generation.",
        });
        return "done";
      }
      return "failed";
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    setJob(null);
    setError(null);

    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    let failures = 0;

    const run = async () => {
      if (!active) return;
      const outcome = await poll();
      if (outcome === "done" || !active) return;

      if (outcome === "failed") {
        failures += 1;
        // Bounded: without this a permanently failing poll rescheduled itself
        // forever, leaving the card spinning and generation locked out.
        if (failures >= MAX_CONSECUTIVE_FAILURES) {
          setLoading(false);
          setError({
            code: "poll_failed",
            message: "Lost contact with the server while tracking this generation.",
          });
          return;
        }
      } else {
        failures = 0;
      }
      timer = setTimeout(run, 2000);
    };

    run();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, poll]);

  return { job, loading, error };
}
